//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, Typography } from '@mui/material'
import React, { ReactElement } from 'react'

import { theme } from '@react/components/theme'

import { TableHeaderItem } from '@react/components/TableHeaderItem'
import { useOtChainTimestamp, useOtDispatch, useOtSelector } from '@react/redux/store'
import {
  assetsAndSharesToExchangeRate,
  dayNumberToDateString,
  erc20WeiToCommaDecString,
  exchangeRateDecimalStringToExchangeRate,
  exchangeRateToDecimalString,
  fromWeiToMWeiERC20NumberString,
  toUTCDateString,
} from '@shared/conversions'
import { EXCHANGE_RATE_ONE } from '@shared/Enums'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import { EthAddr, NumberFormatEnum } from '@shared/SchemaTypes'

import OtAdminNumberField from '@react/components/OtAdminNumberField'
import { dynamicExchangeRateSetTx } from '@react/signedTxs/dynamicSetDynamicExchangeRate'
import { logOT } from '@shared/loggingShared'

export default function DynamicSetDynamicExchangeRateForm(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props

  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)
  const [exchangeRate, setExchangeRate] = React.useState<string>(poolCTO.exchangeRate)
  const [exchangeRateDecimalString, setExchangeRateDecimalString] = React.useState<string>(
    exchangeRateToDecimalString(poolCTO.exchangeRate)
  )
  const [isConfirmed, setIsConfirmed] = React.useState(false)
  const [assetValue, setAssetValue] = React.useState<string>(fromWeiToMWeiERC20NumberString(poolCTO.totalAssets))

  const chainTimestamp = useOtChainTimestamp()
  const dispatch = useOtDispatch()

  const [errorAmount, setErrorAmount] = React.useState<string | undefined>(undefined)

  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }
  function isPositiveNumeric(str: string): boolean {
    const num = Number(str)
    const isValid = !isNaN(num) && isFinite(num) && num > 0

    return isValid
  }
  const setExchangerateAction = (): void => {
    setIsSubmitted(true)
    dispatch(
      dynamicExchangeRateSetTx({
        poolAddr,
        exchangeRate,
      })
    )
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const disableButton = !isPositiveNumeric(assetValue)

  const newAssetsBI = (BigInt(exchangeRate) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE

  if (isConfirmed) {
    return (
      <Grid container sx={{ marginTop: 2, flexDirection: 'column', gap: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h4">Confirm Change Exchange Rate</Typography>

          <Typography variant="h4">
            Please confirm the change of exchange rate from {exchangeRateToDecimalString(poolCTO.exchangeRate)} to{' '}
            {exchangeRateDecimalString}
          </Typography>
          <Typography variant="h4">
            Please confirm the change of assets from {erc20WeiToCommaDecString(poolCTO.totalAssets)} to{' '}
            {erc20WeiToCommaDecString(newAssetsBI)}
          </Typography>

          <Typography variant="h4">
            This is a change of asset value of {erc20WeiToCommaDecString(newAssetsBI - BigInt(poolCTO.totalAssets))}{' '}
            {poolCTO.liquidityTokenSymbol}
          </Typography>
          <Typography variant="h4">
            or {((Number(newAssetsBI - BigInt(poolCTO.totalAssets)) / Number(poolCTO.totalAssets)) * 100).toFixed(2)}%
          </Typography>

          <Button
            variant={'contained'}
            onClick={() => {
              setExchangerateAction()
            }}
          >
            Confirm
          </Button>
        </Grid>
        {isSubmitted && (
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            Submitted ......
          </Typography>
        )}
      </Grid>
    )
  }

  return (
    <Grid container sx={{ marginTop: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <TableHeaderItem>Assets</TableHeaderItem>
        </Grid>
        <Grid item xs={3}>
          <TableHeaderItem>Shares</TableHeaderItem>
        </Grid>
        <Grid item xs={2}>
          <TableHeaderItem>Date</TableHeaderItem>
        </Grid>
        <Grid item xs={4}>
          <TableHeaderItem>Exchange Rate</TableHeaderItem>
        </Grid>
      </Grid>

      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(poolCTO.totalAssets)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {dayNumberToDateString(poolCTO.exchangeRateSetDay)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateToDecimalString(poolCTO.exchangeRate)}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Updated values</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(newAssetsBI)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(chainTimestamp)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateDecimalString}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <OtAdminNumberField
          id="assetValue"
          inputLabel="Updated Asset Value"
          value={assetValue}
          sx={{ width: '300px' }}
          variant="outlined"
          numberFormat={NumberFormatEnum.ERC20}
          error={!!errorAmount}
          helperText={errorAmount}
          onChange={(changedValue: string): void => {
            setAssetValue(changedValue)
            const sanitizedValue = changedValue.replace(/,/g, '')
            const assetvalueNumber = Number(sanitizedValue)

            logOT.info('assetvalueNumber ', assetvalueNumber, changedValue)
            const er = assetsAndSharesToExchangeRate(assetvalueNumber, poolCTO.totalShares)
            setExchangeRateDecimalString(exchangeRateToDecimalString(er))
            setExchangeRate(er.toString())

            if (!isPositiveNumeric(changedValue)) {
              setErrorAmount('Please enter a positive number')
            } else {
              setErrorAmount(undefined)
            }
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <OtAdminNumberField
          id="exchangeRate"
          inputLabel="ExchangeRate"
          numberFormat={NumberFormatEnum.EXCHANGE_RATE}
          value={exchangeRateDecimalString}
          sx={{ width: '300px' }}
          variant="outlined"
          error={!!errorAmount}
          helperText={errorAmount}
          onChange={(changedValue: string): void => {
            setExchangeRateDecimalString(changedValue)
            setExchangeRate(exchangeRateDecimalStringToExchangeRate(changedValue).toString())
            const assets =
              (exchangeRateDecimalStringToExchangeRate(changedValue) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE
            setAssetValue(fromWeiToMWeiERC20NumberString(assets.toString() as `${number}`))
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <Button
          disabled={isSubmitted || disableButton}
          sx={sx}
          onClick={() => {
            setIsConfirmed(true)
          }}
          variant={'contained'}
        >
          <Typography variant={'buttonBold'}>Set Exchange rate</Typography>
        </Button>
      </Grid>
      {disableButton && (
        <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
          Disabled
        </Typography>
      )}
      {isSubmitted && (
        <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
          Submitted ......
        </Typography>
      )}
    </Grid>
  )
}
